// src/service/trainerTime.js
import {
    addDoc,
    collection,
    collectionGroup,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
  } from "firebase/firestore";
  import { db } from "../firebase-config";
  
  /**
   * Starts a work session for trainer. Enforces one active session.
   */
  export async function clockIn({ trainerId, context = {} }) {
    if (!trainerId) throw new Error("clockIn: trainerId required");
  
    // ✅ No composite index needed: remove orderBy here
    const activeQ = query(
      collection(db, `users/${trainerId}/work_sessions`),
      where("status", "==", "active"),
      limit(1)
    );
    const activeSnap = await getDocs(activeQ);
    if (!activeSnap.empty) {
      const d = activeSnap.docs[0];
      return { id: d.id, data: d.data() }; // already active
    }
  
    const nowMs = Date.now();
    const payload = {
      trainerId,
      status: "active",           // "active" | "closed"
      source: "manual",           // optional: "manual" | "live_session"
      context,                    // optional: {packageId,batchId,courseId,moduleId,settingId}
      startedAt: serverTimestamp(),
      startedAtMs: nowMs,
      endedAt: null,
      endedAtMs: null,
      durationMs: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  
    const ref = await addDoc(collection(db, `users/${trainerId}/work_sessions`), payload);
    return { id: ref.id, data: payload };
  }
  
  /**
   * Stops an active session (or a specific sessionId).
   */
  export async function clockOut({ trainerId, sessionId = null }) {
    if (!trainerId) throw new Error("clockOut: trainerId required");
  
    let targetRef, curr;
    if (sessionId) {
      targetRef = doc(db, `users/${trainerId}/work_sessions/${sessionId}`);
      const snap = await getDoc(targetRef);
      curr = snap.exists() ? snap.data() : null;
    } else {
      // ✅ No composite index needed: remove orderBy here
      const activeQ = query(
        collection(db, `users/${trainerId}/work_sessions`),
        where("status", "==", "active"),
        limit(1)
      );
      const snap = await getDocs(activeQ);
      if (snap.empty) return; // nothing to close
      const d = snap.docs[0];
      targetRef = d.ref;
      curr = d.data();
    }
  
    const nowMs = Date.now();
    const startedAtMs = curr?.startedAtMs || nowMs;
    const durationMs = Math.max(0, nowMs - startedAtMs);
  
    await updateDoc(targetRef, {
      status: "closed",
      endedAt: serverTimestamp(),
      endedAtMs: nowMs,
      durationMs,
      updatedAt: serverTimestamp(),
    });
  }
  
  /**
   * Get sessions for a trainer in a date range.
   * (Range filter + orderBy on the same field is fine without composite index.)
   */
  export async function getSessions({ trainerId, fromMs, toMs }) {
    if (!trainerId) throw new Error("getSessions: trainerId required");
    const now = Date.now();
    const from = Number.isFinite(fromMs) ? fromMs : now - 30 * 24 * 3600 * 1000;
    const to = Number.isFinite(toMs) ? toMs : now + 24 * 3600 * 1000;
  
    const qy = query(
      collection(db, `users/${trainerId}/work_sessions`),
      where("startedAtMs", ">=", from),
      where("startedAtMs", "<=", to),
      orderBy("startedAtMs", "desc")
    );
  
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  
  /**
   * Admin: all trainers via collection group.
   */
  export async function adminGetAllSessions({ fromMs, toMs }) {
    const now = Date.now();
    const from = Number.isFinite(fromMs) ? fromMs : now - 7 * 24 * 3600 * 1000;
    const to = Number.isFinite(toMs) ? toMs : now + 24 * 3600 * 1000;
  
    const qy = query(
      collectionGroup(db, "work_sessions"),
      where("startedAtMs", ">=", from),
      where("startedAtMs", "<=", to),
      orderBy("startedAtMs", "desc")
    );
  
    const snap = await getDocs(qy);
    return snap.docs.map((d) => {
      // users/<trainerId>/work_sessions/<docId>
      const path = d.ref.path.split("/");
      const trainerId = path[1];
      return { id: d.id, trainerId, ...d.data() };
    });
  }
  
  export function summarizeByDay(sessions) {
    const pad = (n) => String(n).padStart(2, "0");
    const byDay = {};
    sessions.forEach((s) => {
      const ts = s.startedAtMs || Date.now();
      const d = new Date(ts);
      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      byDay[key] = byDay[key] || { date: key, totalMs: 0, sessions: [] };
      const dur = s.durationMs || (s.status === "active" ? Date.now() - ts : 0);
      byDay[key].totalMs += Math.max(0, dur);
      byDay[key].sessions.push(s);
    });
    return Object.values(byDay).sort((a, b) => (a.date < b.date ? 1 : -1));
  }
  
  export function fmtHMS(ms = 0) {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(ss)}`;
  }
  